import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  Paper,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Select,
  FormControl,
  MenuItem,
  Snackbar,
  Alert,
  Slide,
  useMediaQuery,
  useTheme,
  alpha,
  Tooltip,
  LinearProgress,
  Stack,
  Autocomplete,
  Rating,
  CardActions,
  Menu,
  ListItemIcon,
  ListItemText,
  Pagination,
  FormControlLabel,
  Switch,
  Checkbox,
  TablePagination,
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';

// ============================================
// ICON IMPORTS
// ============================================
import {
  Inventory as InventoryIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Block as BlockIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  FileDownload as FileDownloadIcon,
  GridView as GridViewIcon,
  ViewList as ViewListIcon,
  Star as StarIcon,
  LocalOffer as LocalOfferIcon,
  AccessTime as ClockIcon,
  TrendingDown as TrendingDownIcon,
  Warning as WarningIcon,
  Upload as UploadIcon,
  Image as ImageIcon,
} from '@mui/icons-material';

// Import shared components
import Preloader from '../../theme/Preloader';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Footer from './components/Footer';
import { formatDate, formatCurrency } from '../buyers/components/utils';

// ============================================
// ENHANCED COLOR SCHEME
// ============================================
const COLORS = {
  primary: '#1A237E',
  primaryLight: '#283593',
  primaryDark: '#0D1445',
  secondary: '#00BCD4',
  accent: '#FF6F00',
  success: '#2E7D32',
  warning: '#E65100',
  error: '#C62828',
  info: '#01579B',
  gold: '#D4AF37',
  purple: '#6A1B9A',
  teal: '#00838F',
  grey: {
    50: '#FAFAFA',
    100: '#F5F5F5',
    200: '#EEEEEE',
    300: '#E0E0E0',
    400: '#BDBDBD',
    500: '#9E9E9E',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },
};

// ============================================
// API CONFIGURATION
// ============================================
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
const IMAGE_BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL || 'http://localhost/rawMaterialChemicalDb/uploads/';

const API_ENDPOINTS = {
  PRODUCTS: {
    LIST: `${API_BASE_URL}/rmrdc/products.php`,
    CREATE: `${API_BASE_URL}/rmrdc/products.php`,
    UPDATE: (id) => `${API_BASE_URL}/rmrdc/products.php?id=${id}`,
    DELETE: (id) => `${API_BASE_URL}/rmrdc/products.php?id=${id}`,
    TOGGLE_STATUS: (id) => `${API_BASE_URL}/rmrdc/products.php?action=toggle_status&id=${id}`,
    STATS: `${API_BASE_URL}/rmrdc/products.php?action=stats`,
    EXPORT: `${API_BASE_URL}/rmrdc/products.php?action=export`,
    BULK: `${API_BASE_URL}/rmrdc/products.php?action=bulk`,
  },
};

// ============================================
// LOGGER
// ============================================
const logger = {
  info: (message, data) => console.log(`[PRODUCT-MGMT] ✅ ${message}`, data || ''),
  error: (message, error) => console.error(`[PRODUCT-MGMT] ❌ ${message}`, error || ''),
  debug: (message, data) => console.debug(`[PRODUCT-MGMT] 🔍 ${message}`, data || ''),
  warn: (message, data) => console.warn(`[PRODUCT-MGMT] ⚠️ ${message}`, data || ''),
};

// ============================================
// IMAGE HELPER
// ============================================
const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  const base = IMAGE_BASE_URL.endsWith('/') ? IMAGE_BASE_URL : IMAGE_BASE_URL + '/';
  const cleanPath = imagePath.startsWith('/') ? imagePath.substring(1) : imagePath;
  return `${base}${cleanPath}`;
};

// ============================================
// CUSTOM COMPONENTS
// ============================================
const ProductStatusChip = ({ status }) => {
  const config = {
    active: { label: 'Active', color: COLORS.success, icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> },
    inactive: { label: 'Inactive', color: COLORS.error, icon: <BlockIcon sx={{ fontSize: 14 }} /> },
    pending: { label: 'Pending', color: COLORS.warning, icon: <ClockIcon sx={{ fontSize: 14 }} /> },
    out_of_stock: { label: 'Out of Stock', color: COLORS.grey[600], icon: <WarningIcon sx={{ fontSize: 14 }} /> },
  };

  const { label, color, icon } = config[status] || config.inactive;

  return (
    <Chip
      icon={icon}
      label={label}
      size="small"
      sx={{
        bgcolor: alpha(color, 0.1),
        color: color,
        fontWeight: 600,
        border: `1px solid ${alpha(color, 0.15)}`,
        '& .MuiChip-icon': { color: color },
      }}
    />
  );
};

const ProductTypeChip = ({ type }) => {
  const colors = {
    'Acids': COLORS.error,
    'Bases': COLORS.info,
    'Solvents': COLORS.success,
    'Surfactants': COLORS.warning,
    'Excipients': COLORS.purple,
    'Pharmaceutical Ingredients': COLORS.gold,
    'Chemical Intermediates': COLORS.teal,
  };

  const color = colors[type] || COLORS.grey[600];

  return (
    <Chip
      label={type || 'Uncategorized'}
      size="small"
      sx={{
        bgcolor: alpha(color, 0.1),
        color: color,
        fontWeight: 500,
        border: `1px solid ${alpha(color, 0.15)}`,
      }}
    />
  );
};

// ============================================
// MAIN COMPONENT
// ============================================
const ProductManagement = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  // ============================================
  // LOCAL STORAGE DATA
  // ============================================
  const userData = useMemo(() => {
    try {
      const data = localStorage.getItem('user_data');
      return data ? JSON.parse(data) : null;
    } catch (e) {
      return null;
    }
  }, []);

  const userType = localStorage.getItem('user_type') || 'super_admin';
  const isAdmin = userType === 'super_admin' || userType === 'rmrdc';

  // ============================================
  // STATE
  // ============================================
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(12);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [selectedTab, setSelectedTab] = useState('products');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [bulkAction, setBulkAction] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [productTypes, setProductTypes] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const [anchorEl, setAnchorEl] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [imageUploadProgress, setImageUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  // Dialog states
  const [productDetailsDialog, setProductDetailsDialog] = useState(false);
  const [createProductDialog, setCreateProductDialog] = useState(false);
  const [editProductDialog, setEditProductDialog] = useState(false);
  const [deleteProductDialog, setDeleteProductDialog] = useState(false);
  const [bulkActionDialog, setBulkActionDialog] = useState(false);
  const [imageUploadDialog, setImageUploadDialog] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    productType: '',
    applications: '',
    hazardClassifications: '',
    registryNumber: '',
    Synonyms: '',
    ChemicalFormula: '',
    Grade: '',
    otherGrade: '',
    ManufacturerName: '',
    CountryOrigin: '',
    capacity: '',
    DeliveryTime: '',
    Certifications: '',
    price: '',
    currency: 'NGN',
    discounts: 0,
    EnvironmentalImpact: '',
    LifecycleDisposal: '',
    msds: '',
    HealthRisks: '',
    hs_code: '',
    import_export_info: '',
    unit_of_measure: 'kg',
    min_order_quantity: 1,
    quantity_in_stock: 0,
    status: 'active',
    tags: [],
    specifications: [],
    image: '',
  });

  // ============================================
  // API FUNCTIONS
  // ============================================
  const fetchProducts = useCallback(async (page = 1, search = searchTerm) => {
    logger.info('Fetching products...');
    setLoading(true);

    try {
      const userDataStr = localStorage.getItem('user_data');
      if (!userDataStr) {
        throw new Error('User data not found. Please login again.');
      }

      const userData = JSON.parse(userDataStr);
      const userId = userData.id;

      if (!userId) {
        throw new Error('User ID not found. Please login again.');
      }

      const params = new URLSearchParams();
      params.append('user_id', userId);
      params.append('page', page);
      params.append('limit', rowsPerPage);

      if (search) params.append('search', search);
      if (filterType !== 'all') params.append('productType', filterType);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      
      const sortMap = {
        'newest': 'created_at',
        'oldest': 'created_at_asc',
        'price_low': 'price',
        'price_high': 'price_desc',
        'rating': 'average_rating',
        'popular': 'view_count',
      };
      const sortValue = sortMap[sortBy] || 'created_at';
      params.append('sort_by', sortValue);

      const url = `${API_ENDPOINTS.PRODUCTS.LIST}?${params.toString()}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (result.success && result.data) {
        let productsData = [];
        
        if (Array.isArray(result.data)) {
          productsData = result.data;
        } else if (result.data.products) {
          productsData = result.data.products;
        } else if (result.data.data) {
          productsData = result.data.data;
        }
        
        setProducts(productsData);
        setFilteredProducts(productsData);
        setTotalCount(result.meta?.total || productsData.length);
        setCurrentPage(page);
        
        const types = [...new Set(productsData.map(p => p.productType).filter(Boolean))];
        setProductTypes(types);
        
        logger.info(`✅ Loaded ${productsData.length} products`);
      } else {
        throw new Error(result.message || 'Failed to fetch products');
      }
    } catch (error) {
      logger.error('Error fetching products:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to load products. Please try again.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [filterType, filterStatus, sortBy, rowsPerPage]);

  const createProduct = useCallback(async () => {
    try {
      const userDataStr = localStorage.getItem('user_data');
      if (!userDataStr) return;

      const userData = JSON.parse(userDataStr);
      const userId = userData.id;

      const response = await fetch(`${API_ENDPOINTS.PRODUCTS.CREATE}?user_id=${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();
      
      if (result.success) {
        setSnackbar({ open: true, message: 'Product created successfully', severity: 'success' });
        setCreateProductDialog(false);
        resetForm();
        fetchProducts(currentPage);
      } else {
        throw new Error(result.message || 'Failed to create product');
      }
    } catch (error) {
      logger.error('Error creating product:', error);
      setSnackbar({ open: true, message: error.message || 'Failed to create product', severity: 'error' });
    }
  }, [formData, fetchProducts, currentPage]);

  const updateProduct = useCallback(async () => {
    try {
      const userDataStr = localStorage.getItem('user_data');
      if (!userDataStr) return;

      const userData = JSON.parse(userDataStr);
      const userId = userData.id;

      const response = await fetch(`${API_ENDPOINTS.PRODUCTS.UPDATE(selectedProduct?.id)}?user_id=${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();
      
      if (result.success) {
        setSnackbar({ open: true, message: 'Product updated successfully', severity: 'success' });
        setEditProductDialog(false);
        setProductDetailsDialog(false);
        fetchProducts(currentPage);
      } else {
        throw new Error(result.message || 'Failed to update product');
      }
    } catch (error) {
      logger.error('Error updating product:', error);
      setSnackbar({ open: true, message: error.message || 'Failed to update product', severity: 'error' });
    }
  }, [formData, selectedProduct, fetchProducts, currentPage]);

  const deleteProduct = useCallback(async (productId) => {
    if (!productId) {
      setSnackbar({ open: true, message: 'Invalid product ID', severity: 'error' });
      return;
    }

    try {
      const userDataStr = localStorage.getItem('user_data');
      if (!userDataStr) {
        throw new Error('User data not found. Please login again.');
      }

      const userData = JSON.parse(userDataStr);
      const adminId = userData.id;

      if (!adminId) {
        throw new Error('Admin ID not found. Please login again.');
      }

      setSnackbar({ open: true, message: 'Deleting product...', severity: 'info' });

      const response = await fetch(`${API_ENDPOINTS.PRODUCTS.DELETE(productId)}&user_id=${adminId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      
      if (result.success) {
        setSnackbar({ open: true, message: result.message || 'Product deleted successfully', severity: 'success' });
        setDeleteProductDialog(false);
        setSelectedProduct(null);
        fetchProducts(currentPage);
      } else {
        throw new Error(result.message || 'Failed to delete product');
      }
    } catch (error) {
      logger.error('Error deleting product:', error);
      setSnackbar({ open: true, message: error.message || 'Failed to delete product', severity: 'error' });
    }
  }, [fetchProducts, currentPage]);

  const toggleProductStatus = useCallback(async (productId, status) => {
    try {
      const userDataStr = localStorage.getItem('user_data');
      if (!userDataStr) return;

      const userData = JSON.parse(userDataStr);
      const adminId = userData.id;

      const response = await fetch(`${API_ENDPOINTS.PRODUCTS.TOGGLE_STATUS(productId)}&user_id=${adminId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status }),
      });

      const result = await response.json();
      
      if (result.success) {
        setSnackbar({ open: true, message: `Product status updated to ${status}`, severity: 'success' });
        fetchProducts(currentPage);
      } else {
        throw new Error(result.message || 'Failed to update product status');
      }
    } catch (error) {
      logger.error('Error toggling product status:', error);
      setSnackbar({ open: true, message: error.message || 'Failed to update product status', severity: 'error' });
    }
  }, [fetchProducts, currentPage]);

  const uploadProductImage = useCallback(async (file, productId) => {
    if (!file) {
      setSnackbar({ open: true, message: 'No file selected', severity: 'error' });
      return;
    }

    setImageUploading(true);
    setImageUploadProgress(0);

    try {
      const userDataStr = localStorage.getItem('user_data');
      if (!userDataStr) return;

      const userData = JSON.parse(userDataStr);
      const userId = userData.id;

      const formData = new FormData();
      formData.append('image', file);
      formData.append('product_id', productId);
      formData.append('user_id', userId);
      formData.append('is_primary', 'true');

      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const progress = Math.round((e.loaded / e.total) * 100);
          setImageUploadProgress(progress);
        }
      });

      const response = await new Promise((resolve, reject) => {
        xhr.open('POST', API_ENDPOINTS.PRODUCTS.UPLOAD_IMAGE);
        xhr.onload = () => {
          if (xhr.status === 200) {
            resolve(JSON.parse(xhr.response));
          } else {
            reject(new Error('Upload failed'));
          }
        };
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(formData);
      });

      if (response.success) {
        setSnackbar({ open: true, message: 'Image uploaded successfully', severity: 'success' });
        setImageUploadDialog(false);
        fetchProducts(currentPage);
      } else {
        throw new Error(response.message || 'Failed to upload image');
      }
    } catch (error) {
      logger.error('Error uploading image:', error);
      setSnackbar({ open: true, message: error.message || 'Failed to upload image', severity: 'error' });
    } finally {
      setImageUploading(false);
      setImageUploadProgress(0);
    }
  }, [fetchProducts, currentPage]);

  const bulkUpdateProducts = useCallback(async () => {
    try {
      const userDataStr = localStorage.getItem('user_data');
      if (!userDataStr) return;

      const userData = JSON.parse(userDataStr);
      const userId = userData.id;

      const response = await fetch(`${API_ENDPOINTS.PRODUCTS.BULK}&user_id=${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          bulk_action: bulkAction,
          product_ids: selectedProducts,
        }),
      });

      const result = await response.json();
      
      if (result.success) {
        setSnackbar({ open: true, message: result.message, severity: 'success' });
        setBulkActionDialog(false);
        setSelectedProducts([]);
        setBulkAction('');
        fetchProducts(currentPage);
      } else {
        throw new Error(result.message || 'Failed to perform bulk action');
      }
    } catch (error) {
      logger.error('Error performing bulk action:', error);
      setSnackbar({ open: true, message: error.message || 'Failed to perform bulk action', severity: 'error' });
    }
  }, [bulkAction, selectedProducts, fetchProducts, currentPage]);

  const exportProducts = useCallback(async () => {
    try {
      const userDataStr = localStorage.getItem('user_data');
      if (!userDataStr) return;

      const userData = JSON.parse(userDataStr);
      const userId = userData.id;

      const response = await fetch(`${API_ENDPOINTS.PRODUCTS.EXPORT}&user_id=${userId}&format=csv`, {
        method: 'GET',
        headers: {
          'Content-Type': 'text/csv',
        },
      });

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `products_export_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);

      setSnackbar({ open: true, message: 'Products exported successfully', severity: 'success' });
    } catch (error) {
      logger.error('Error exporting products:', error);
      setSnackbar({ open: true, message: 'Failed to export products', severity: 'error' });
    }
  }, []);

  // ============================================
  // FORM HANDLERS
  // ============================================
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      productType: '',
      applications: '',
      hazardClassifications: '',
      registryNumber: '',
      Synonyms: '',
      ChemicalFormula: '',
      Grade: '',
      otherGrade: '',
      ManufacturerName: '',
      CountryOrigin: '',
      capacity: '',
      DeliveryTime: '',
      Certifications: '',
      price: '',
      currency: 'NGN',
      discounts: 0,
      EnvironmentalImpact: '',
      LifecycleDisposal: '',
      msds: '',
      HealthRisks: '',
      hs_code: '',
      import_export_info: '',
      unit_of_measure: 'kg',
      min_order_quantity: 1,
      quantity_in_stock: 0,
      status: 'active',
      tags: [],
      specifications: [],
      image: '',
    });
  };

  const handleFormChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleTagChange = (event, newTags) => {
    setFormData(prev => ({
      ...prev,
      tags: newTags,
    }));
  };

  const handleSpecificationChange = (index, field, value) => {
    const newSpecs = [...formData.specifications];
    newSpecs[index] = { ...newSpecs[index], [field]: value };
    setFormData(prev => ({
      ...prev,
      specifications: newSpecs,
    }));
  };

  const addSpecification = () => {
    setFormData(prev => ({
      ...prev,
      specifications: [...prev.specifications, { key: '', value: '' }],
    }));
  };

  const removeSpecification = (index) => {
    setFormData(prev => ({
      ...prev,
      specifications: prev.specifications.filter((_, i) => i !== index),
    }));
  };

  const handleViewProduct = (product) => {
    setSelectedProduct(product);
    setProductDetailsDialog(true);
  };

  const handleEditProduct = (product) => {
    setSelectedProduct(product);
    setFormData({
      name: product.name || '',
      description: product.description || '',
      productType: product.productType || '',
      applications: product.applications || '',
      hazardClassifications: product.hazardClassifications || '',
      registryNumber: product.registryNumber || '',
      Synonyms: product.Synonyms || '',
      ChemicalFormula: product.ChemicalFormula || '',
      Grade: product.Grade || '',
      otherGrade: product.otherGrade || '',
      ManufacturerName: product.ManufacturerName || '',
      CountryOrigin: product.CountryOrigin || '',
      capacity: product.capacity || '',
      DeliveryTime: product.DeliveryTime || '',
      Certifications: product.Certifications || '',
      price: product.price || '',
      currency: product.currency || 'NGN',
      discounts: product.discounts || 0,
      EnvironmentalImpact: product.EnvironmentalImpact || '',
      LifecycleDisposal: product.LifecycleDisposal || '',
      msds: product.msds || '',
      HealthRisks: product.HealthRisks || '',
      hs_code: product.hs_code || '',
      import_export_info: product.import_export_info || '',
      unit_of_measure: product.unit_of_measure || 'kg',
      min_order_quantity: product.min_order_quantity || 1,
      quantity_in_stock: product.quantity_in_stock || 0,
      status: product.status || 'active',
      tags: product.tags ? product.tags.split(', ') : [],
      specifications: product.specifications || [],
      image: product.image || '',
    });
    setEditProductDialog(true);
  };

  const handleDeleteProduct = (product) => {
    setSelectedProduct(product);
    setDeleteProductDialog(true);
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedProducts(filteredProducts.map(p => p.id));
    } else {
      setSelectedProducts([]);
    }
  };

  const handleSelectProduct = (productId) => {
    setSelectedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId) 
        : [...prev, productId]
    );
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    fetchProducts(newPage + 1);
  };

  const handleRowsPerPageChange = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchProducts(1);
  };

  const handleSearch = useCallback((value) => {
    setSearchTerm(value);
    fetchProducts(1, value);
  }, [fetchProducts]);

  useEffect(() => {
  const token = localStorage.getItem('access_token');
  if (!token || (userType !== 'super_admin' && userType !== 'rmrdc' && userType !== 'supplier')) {
    navigate('/RawAuth');
    return;
  }
  fetchProducts(1);
}, [fetchProducts, navigate, userType]);
  // ============================================
  // RENDER FUNCTIONS
  // ============================================
 

  const renderFilters = () => (
    <Paper sx={{ p: 2.5, mb: 3.5, borderRadius: 4, border: `1px solid ${COLORS.grey[200]}`, background: '#FFFFFF' }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <TextField
          placeholder="Search products by name, description..."
          size="small"
          value={searchTerm}
          onChange={(e) => handleSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: COLORS.grey[500] }} />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => handleSearch('')}>
                  <CloseIcon fontSize="small" sx={{ color: COLORS.grey[500] }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 200, flex: 1 }}
        />
        
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <Select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            displayEmpty
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            <MenuItem value="all">All Types</MenuItem>
            {productTypes.map(type => (
              <MenuItem key={type} value={type}>{type}</MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <Select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            displayEmpty
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="out_of_stock">Out of Stock</MenuItem>
          </Select>
        </FormControl>
        
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <Select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            displayEmpty
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            <MenuItem value="newest">Newest First</MenuItem>
            <MenuItem value="oldest">Oldest First</MenuItem>
            <MenuItem value="price_low">Price: Low to High</MenuItem>
            <MenuItem value="price_high">Price: High to Low</MenuItem>
            <MenuItem value="rating">Top Rated</MenuItem>
            <MenuItem value="popular">Most Viewed</MenuItem>
          </Select>
        </FormControl>
        
        <Box sx={{ display: 'flex', gap: 1, ml: 'auto', flexWrap: 'wrap' }}>
          <Tooltip title="View Mode">
            <IconButton onClick={() => setViewMode(viewMode === 'table' ? 'grid' : 'table')} size="small">
              {viewMode === 'table' ? <GridViewIcon /> : <ViewListIcon />}
            </IconButton>
          </Tooltip>
        
          <Tooltip title="Refresh">
            <IconButton onClick={() => fetchProducts(currentPage)} size="small" sx={{ bgcolor: COLORS.grey[100] }}>
              <RefreshIcon sx={{ color: COLORS.grey[700] }} />
            </IconButton>
          </Tooltip>
        
        </Box>
      </Box>
    </Paper>
  );

  const renderGridView = () => {
    const displayProducts = filteredProducts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
      <Box>
        <Grid container spacing={2.5}>
          {displayProducts.map((product) => {
            const imageUrl = getImageUrl(product.image);
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4} key={product.id}>
                <Card sx={{ 
                  borderRadius: 3, 
                  overflow: 'hidden',
                  transition: 'all 0.25s ease',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[8],
                  }
                }}>
                  {/* Image Section */}
                  <Box sx={{ 
                    position: 'relative', 
                    height: 160, 
                    bgcolor: COLORS.grey[100],
                    flexShrink: 0,
                  }}>
                    {imageUrl ? (
                      <img 
                        src={imageUrl} 
                        alt={product.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.querySelector('.fallback-icon')?.classList.remove('hidden');
                        }}
                      />
                    ) : null}
                    <Box className="fallback-icon" sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      height: '100%',
                      flexDirection: 'column',
                      color: COLORS.grey[400],
                      ...(imageUrl ? { display: 'none' } : {})
                    }}>
                      <InventoryIcon sx={{ fontSize: 40 }} />
                      <Typography variant="caption">No Image</Typography>
                    </Box>

                    {/* Status Badge - Top Right */}
                    <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                      <ProductStatusChip status={product.status} />
                    </Box>

                    {/* Discount Badge - Top Left */}
                    {product.discounts > 0 && (
                      <Box sx={{ 
                        position: 'absolute', 
                        top: 8, 
                        left: 8, 
                        bgcolor: COLORS.gold,
                        color: '#fff',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1.5,
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                      }}>
                        {product.discounts}% OFF
                      </Box>
                    )}
                  </Box>

                  {/* Content Section */}
                  <CardContent sx={{ flex: 1, p: 2, pb: 0.5 }}>
                    <Typography variant="subtitle2" fontWeight={600} noWrap title={product.name}>
                      {product.name}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ 
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      height: 36,
                      fontSize: '0.75rem',
                      mt: 0.5,
                    }}>
                      {product.description}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                      <ProductTypeChip type={product.productType} />
                      <Chip
                        label={`Stock: ${product.quantity_in_stock || 0}`}
                        size="small"
                        sx={{ 
                          height: 20, 
                          fontSize: '0.6rem',
                          bgcolor: product.quantity_in_stock <= 5 && product.quantity_in_stock > 0 ? alpha(COLORS.warning, 0.1) : 'transparent',
                          color: product.quantity_in_stock <= 5 && product.quantity_in_stock > 0 ? COLORS.warning : COLORS.grey[600],
                          border: `1px solid ${product.quantity_in_stock <= 5 && product.quantity_in_stock > 0 ? alpha(COLORS.warning, 0.2) : COLORS.grey[200]}`,
                        }}
                      />
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                      <Box>
                        <Typography variant="h6" fontWeight={700} color={COLORS.primary} sx={{ fontSize: '1rem' }}>
                          {formatCurrency(product.discountedPrice || product.price, product.currency)}
                        </Typography>
                        {product.discounts > 0 && (
                          <Typography variant="caption" color="textSecondary" sx={{ textDecoration: 'line-through', fontSize: '0.65rem' }}>
                            {formatCurrency(product.price, product.currency)}
                          </Typography>
                        )}
                      </Box>
                      <Rating value={product.average_rating || 0} readOnly size="small" precision={0.5} />
                    </Box>
                  </CardContent>

                  {/* Actions Section */}
                  <CardActions sx={{ px: 2, pb: 2, pt: 1, gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<VisibilityIcon />}
                      onClick={() => handleViewProduct(product)}
                      sx={{ flex: 1, borderRadius: 2, textTransform: 'none', fontSize: '0.7rem' }}
                    >
                      View
                    </Button>
                  
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={product.status === 'active' ? <BlockIcon /> : <CheckCircleIcon />}
                      onClick={() => toggleProductStatus(product.id, product.status === 'active' ? 'inactive' : 'active')}
                      sx={{ 
                        flex: 1, 
                        borderRadius: 2, 
                        textTransform: 'none', 
                        fontSize: '0.7rem',
                        bgcolor: product.status === 'active' ? COLORS.warning : COLORS.success,
                        '&:hover': { 
                          bgcolor: product.status === 'active' ? COLORS.warning : COLORS.success,
                          opacity: 0.85,
                        },
                      }}
                    >
                      {product.status === 'active' ? 'Deactivate' : 'Activate'}
                    </Button>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteProduct(product)}
                      sx={{ 
                        border: `1px solid ${alpha(COLORS.error, 0.2)}`,
                        borderRadius: 2,
                        '&:hover': { bgcolor: alpha(COLORS.error, 0.05) },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* Pagination */}
        {totalCount > rowsPerPage && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <TablePagination
              rowsPerPageOptions={[8, 12, 24, 48]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </Box>
        )}
      </Box>
    );
  };

  const renderTableView = () => (
    <TableContainer component={Paper} sx={{ borderRadius: 4, border: `1px solid ${COLORS.grey[200]}` }}>
      <Table>
        <TableHead sx={{ bgcolor: COLORS.grey[50] }}>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                checked={selectedProducts.length === filteredProducts.length && filteredProducts.length > 0}
                indeterminate={selectedProducts.length > 0 && selectedProducts.length < filteredProducts.length}
                onChange={handleSelectAll}
              />
            </TableCell>
            <TableCell sx={{ fontWeight: 700, color: COLORS.grey[700] }}>Product</TableCell>
            <TableCell sx={{ fontWeight: 700, color: COLORS.grey[700] }}>Type</TableCell>
            <TableCell sx={{ fontWeight: 700, color: COLORS.grey[700] }}>Price</TableCell>
            <TableCell sx={{ fontWeight: 700, color: COLORS.grey[700] }}>Stock</TableCell>
            <TableCell sx={{ fontWeight: 700, color: COLORS.grey[700] }}>Status</TableCell>
            <TableCell sx={{ fontWeight: 700, color: COLORS.grey[700] }}>Rating</TableCell>
            <TableCell sx={{ fontWeight: 700, color: COLORS.grey[700] }} align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredProducts.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                <Typography color="textSecondary" fontWeight={500}>No products found</Typography>
              </TableCell>
            </TableRow>
          ) : (
            filteredProducts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((product) => {
              const imageUrl = getImageUrl(product.image);
              return (
                <TableRow key={product.id} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedProducts.includes(product.id)}
                      onChange={() => handleSelectProduct(product.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar
                        src={imageUrl}
                        sx={{ width: 36, height: 36, bgcolor: alpha(COLORS.primary, 0.08) }}
                      >
                        {!imageUrl && <InventoryIcon sx={{ color: COLORS.primary, fontSize: 18 }} />}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 180 }}>
                          {product.name}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" noWrap>
                          {product.registryNumber}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <ProductTypeChip type={product.productType} />
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {formatCurrency(product.discountedPrice || product.price, product.currency)}
                      </Typography>
                      {product.discounts > 0 && (
                        <Typography variant="caption" color="textSecondary" sx={{ textDecoration: 'line-through' }}>
                          {formatCurrency(product.price, product.currency)}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" fontWeight={500}>
                        {product.quantity_in_stock || 0}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {product.unit_of_measure}
                      </Typography>
                      {product.quantity_in_stock <= 5 && product.quantity_in_stock > 0 && (
                        <Chip label="Low" size="small" color="warning" sx={{ height: 18, fontSize: '0.55rem' }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <ProductStatusChip status={product.status} />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Rating value={product.average_rating || 0} readOnly size="small" precision={0.5} />
                      <Typography variant="caption" color="textSecondary">
                        ({product.total_reviews || 0})
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => handleViewProduct(product)} sx={{ color: COLORS.primary }}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    
                      <Tooltip title={product.status === 'active' ? 'Deactivate' : 'Activate'}>
                        <IconButton 
                          size="small" 
                          onClick={() => toggleProductStatus(product.id, product.status === 'active' ? 'inactive' : 'active')}
                          sx={{ color: product.status === 'active' ? COLORS.warning : COLORS.success }}
                        >
                          {product.status === 'active' ? <BlockIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => handleDeleteProduct(product)} sx={{ color: COLORS.error }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[8, 12, 24, 48]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    </TableContainer>
  );

  // ============================================
  // MAIN RENDER
  // ============================================
  if (loading && products.length === 0) {
    return <Preloader text="Loading Products..." size={60} fullScreen />;
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: COLORS.grey[50] }}>
    <Sidebar
  open={drawerOpen}
  onClose={() => setDrawerOpen(false)}
  isMobile={isMobile}
  selectedTab={selectedTab}
  onTabChange={setSelectedTab}  // ← Just update the state
  userData={userData}
  onLogout={() => {
    localStorage.clear();
    navigate('/RawAuth');
  }}
/>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          width: { xs: '100%', md: 'calc(100% - 280px)' },
        }}
      >
        <Header
          isMobile={isMobile}
          onDrawerToggle={() => setDrawerOpen(true)}
          selectedTab={selectedTab}
          menuItems={[]}
          searchTerm={searchTerm}
          onSearchChange={(value) => {
            setSearchTerm(value);
            fetchProducts(1, value);
          }}
          cartItemCount={0}
          onCartOpen={() => {}}
          notifications={[]}
          userData={userData}
          onLogout={() => {
            localStorage.clear();
            navigate('/RawAuth');
          }}
          onChangePassword={() => {}}
          onSettings={() => navigate('/rmrdc/settings')}
        />

        <Box sx={{ flex: 1, p: { xs: 2, sm: 3 }, overflow: 'auto' }}>
          <Box sx={{ mb: 3.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="h4" fontWeight={800} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <InventoryIcon sx={{ color: COLORS.primary, fontSize: 32 }} />
                  Product Management
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {totalCount} products found • Last updated: {lastRefreshed.toLocaleString()}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                {selectedProducts.length > 0 && (
                  <Button
                    variant="outlined"
                    onClick={() => setBulkActionDialog(true)}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                  >
                    Bulk Action ({selectedProducts.length})
                  </Button>
                )}
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => fetchProducts(currentPage)}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Refresh
                </Button>
            
              </Box>
            </Box>
          </Box>

     
          {renderFilters()}
          {viewMode === 'table' ? renderTableView() : renderGridView()}
        </Box>

        <Footer />
      </Box>

      {/* ============================================
          DIALOGS
          ============================================ */}
      
      {/* Product Details Dialog */}
      <Dialog
        open={productDetailsDialog}
        onClose={() => setProductDetailsDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4, maxHeight: '90vh' } }}
      >
        {selectedProduct && (
          <>
            <DialogTitle sx={{ p: 3, pb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar
                    src={getImageUrl(selectedProduct.image)}
                    sx={{ width: 56, height: 56, bgcolor: alpha(COLORS.primary, 0.08) }}
                  >
                    {!selectedProduct.image && <InventoryIcon sx={{ color: COLORS.primary, fontSize: 32 }} />}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={700}>
                      {selectedProduct.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="caption" color="textSecondary">
                        {selectedProduct.registryNumber}
                      </Typography>
                      <ProductStatusChip status={selectedProduct.status} />
                      <ProductTypeChip type={selectedProduct.productType} />
                    </Box>
                  </Box>
                </Box>
                <IconButton onClick={() => setProductDetailsDialog(false)}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper sx={{ p: 2, bgcolor: COLORS.grey[50], borderRadius: 3 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
                      Product Image
                    </Typography>
                    <Box>
                      {getImageUrl(selectedProduct.image) ? (
                        <Box
                          sx={{
                            width: '100%',
                            height: 200,
                            borderRadius: 2,
                            overflow: 'hidden',
                            border: `1px solid ${COLORS.grey[300]}`,
                          }}
                        >
                          <img
                            src={getImageUrl(selectedProduct.image)}
                            alt={selectedProduct.name}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        </Box>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          No image available
                        </Typography>
                      )}
                    </Box>
                  </Paper>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper sx={{ p: 2, bgcolor: COLORS.grey[50], borderRadius: 3 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
                      Product Information
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="textSecondary">Price:</Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {formatCurrency(selectedProduct.discountedPrice || selectedProduct.price, selectedProduct.currency)}
                          {selectedProduct.discounts > 0 && (
                            <Typography component="span" variant="caption" color="textSecondary" sx={{ textDecoration: 'line-through', ml: 1 }}>
                              {formatCurrency(selectedProduct.price, selectedProduct.currency)}
                            </Typography>
                          )}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="textSecondary">Stock:</Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {selectedProduct.quantity_in_stock || 0} {selectedProduct.unit_of_measure}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="textSecondary">Manufacturer:</Typography>
                        <Typography variant="body2">{selectedProduct.ManufacturerName}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" color="textSecondary">Origin:</Typography>
                        <Typography variant="body2">{selectedProduct.CountryOrigin}</Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Paper sx={{ p: 2, bgcolor: COLORS.grey[50], borderRadius: 3 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                      Description
                    </Typography>
                    <Typography variant="body2">{selectedProduct.description}</Typography>
                  </Paper>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper sx={{ p: 2, bgcolor: COLORS.grey[50], borderRadius: 3 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                      Safety & Compliance
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="body2"><strong>Hazard:</strong> {selectedProduct.hazardClassifications || 'N/A'}</Typography>
                      <Typography variant="body2"><strong>Health Risks:</strong> {selectedProduct.HealthRisks || 'N/A'}</Typography>
                      <Typography variant="body2"><strong>Certifications:</strong> {selectedProduct.Certifications || 'N/A'}</Typography>
                    </Box>
                  </Paper>
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <Paper sx={{ p: 2, bgcolor: COLORS.grey[50], borderRadius: 3 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                      Environmental Impact
                    </Typography>
                    <Typography variant="body2">{selectedProduct.EnvironmentalImpact || 'N/A'}</Typography>
                  </Paper>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2.5 }}>
              <Button onClick={() => setProductDetailsDialog(false)}>Close</Button>
            
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Create/Edit Product Dialog - Keeping full form */}
      <Dialog
        open={createProductDialog || editProductDialog}
        onClose={() => {
          setCreateProductDialog(false);
          setEditProductDialog(false);
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4, maxHeight: '90vh' } }}
      >
        <DialogTitle sx={{ p: 3, pb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight={700}>
              {createProductDialog ? (
                <><AddIcon sx={{ verticalAlign: 'middle', mr: 1, color: COLORS.primary }} /> Create New Product</>
              ) : (
                <><EditIcon sx={{ verticalAlign: 'middle', mr: 1, color: COLORS.info }} /> Edit Product</>
              )}
            </Typography>
            <IconButton onClick={() => {
              setCreateProductDialog(false);
              setEditProductDialog(false);
            }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Grid container spacing={2.5}>
            {/* Basic Information */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ color: COLORS.primary, mb: 2 }}>
                Basic Information
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Product Name"
                name="name"
                value={formData.name}
                onChange={handleFormChange}
                fullWidth
                required
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Registry Number"
                name="registryNumber"
                value={formData.registryNumber}
                onChange={handleFormChange}
                fullWidth
                size="small"
                placeholder="CAS or Product Reference"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleFormChange}
                fullWidth
                required
                multiline
                rows={3}
                size="small"
              />
            </Grid>

            {/* Classification */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ color: COLORS.primary, mb: 2, mt: 1 }}>
                Classification
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth size="small">
                <Select
                  label="Product Type"
                  name="productType"
                  value={formData.productType}
                  onChange={handleFormChange}
                  displayEmpty
                >
                  <MenuItem value="">Select Type</MenuItem>
                  <MenuItem value="Acids">Acids</MenuItem>
                  <MenuItem value="Bases">Bases</MenuItem>
                  <MenuItem value="Solvents">Solvents</MenuItem>
                  <MenuItem value="Surfactants">Surfactants</MenuItem>
                  <MenuItem value="Excipients">Excipients</MenuItem>
                  <MenuItem value="Pharmaceutical Ingredients">Pharmaceutical Ingredients</MenuItem>
                  <MenuItem value="Chemical Intermediates">Chemical Intermediates</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                label="Applications"
                name="applications"
                value={formData.applications}
                onChange={handleFormChange}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                label="Hazard Classifications"
                name="hazardClassifications"
                value={formData.hazardClassifications}
                onChange={handleFormChange}
                fullWidth
                size="small"
              />
            </Grid>

            {/* Chemical Properties */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ color: COLORS.primary, mb: 2, mt: 1 }}>
                Chemical Properties
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                label="Chemical Formula"
                name="ChemicalFormula"
                value={formData.ChemicalFormula}
                onChange={handleFormChange}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                label="Synonyms"
                name="Synonyms"
                value={formData.Synonyms}
                onChange={handleFormChange}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                label="Grade"
                name="Grade"
                value={formData.Grade}
                onChange={handleFormChange}
                fullWidth
                size="small"
              />
            </Grid>

            {/* Manufacturer Information */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ color: COLORS.primary, mb: 2, mt: 1 }}>
                Manufacturer Information
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Manufacturer Name"
                name="ManufacturerName"
                value={formData.ManufacturerName}
                onChange={handleFormChange}
                fullWidth
                required
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Country of Origin"
                name="CountryOrigin"
                value={formData.CountryOrigin}
                onChange={handleFormChange}
                fullWidth
                required
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Production Capacity"
                name="capacity"
                value={formData.capacity}
                onChange={handleFormChange}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Delivery Time"
                name="DeliveryTime"
                value={formData.DeliveryTime}
                onChange={handleFormChange}
                fullWidth
                size="small"
                placeholder="e.g., 2-3 weeks"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Certifications"
                name="Certifications"
                value={formData.Certifications}
                onChange={handleFormChange}
                fullWidth
                required
                size="small"
                placeholder="e.g., REACH, FDA, ISO"
              />
            </Grid>

            {/* Pricing & Inventory */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ color: COLORS.primary, mb: 2, mt: 1 }}>
                Pricing & Inventory
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                label="Price"
                name="price"
                type="number"
                value={formData.price}
                onChange={handleFormChange}
                fullWidth
                required
                size="small"
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth size="small">
                <Select
                  label="Currency"
                  name="currency"
                  value={formData.currency}
                  onChange={handleFormChange}
                >
                  <MenuItem value="NGN">NGN (₦)</MenuItem>
                  <MenuItem value="USD">USD ($)</MenuItem>
                  <MenuItem value="EUR">EUR (€)</MenuItem>
                  <MenuItem value="GBP">GBP (£)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                label="Discount (%)"
                name="discounts"
                type="number"
                value={formData.discounts}
                onChange={handleFormChange}
                fullWidth
                size="small"
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                label="Stock Quantity"
                name="quantity_in_stock"
                type="number"
                value={formData.quantity_in_stock}
                onChange={handleFormChange}
                fullWidth
                required
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth size="small">
                <Select
                  label="Unit of Measure"
                  name="unit_of_measure"
                  value={formData.unit_of_measure}
                  onChange={handleFormChange}
                >
                  <MenuItem value="kg">Kilogram (kg)</MenuItem>
                  <MenuItem value="g">Gram (g)</MenuItem>
                  <MenuItem value="l">Liter (L)</MenuItem>
                  <MenuItem value="ml">Milliliter (mL)</MenuItem>
                  <MenuItem value="ton">Ton (t)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                label="Min Order Quantity"
                name="min_order_quantity"
                type="number"
                value={formData.min_order_quantity}
                onChange={handleFormChange}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth size="small">
                <Select
                  label="Status"
                  name="status"
                  value={formData.status}
                  onChange={handleFormChange}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="out_of_stock">Out of Stock</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Safety Information */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ color: COLORS.primary, mb: 2, mt: 1 }}>
                Safety & Compliance
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="MSDS Information"
                name="msds"
                value={formData.msds}
                onChange={handleFormChange}
                fullWidth
                required
                multiline
                rows={3}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Health Risks"
                name="HealthRisks"
                value={formData.HealthRisks}
                onChange={handleFormChange}
                fullWidth
                required
                multiline
                rows={3}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Environmental Impact"
                name="EnvironmentalImpact"
                value={formData.EnvironmentalImpact}
                onChange={handleFormChange}
                fullWidth
                required
                multiline
                rows={3}
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Lifecycle & Disposal"
                name="LifecycleDisposal"
                value={formData.LifecycleDisposal}
                onChange={handleFormChange}
                fullWidth
                required
                multiline
                rows={3}
                size="small"
              />
            </Grid>

            {/* Additional Information */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ color: COLORS.primary, mb: 2, mt: 1 }}>
                Additional Information
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="HS Code"
                name="hs_code"
                value={formData.hs_code}
                onChange={handleFormChange}
                fullWidth
                size="small"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                label="Import/Export Info"
                name="import_export_info"
                value={formData.import_export_info}
                onChange={handleFormChange}
                fullWidth
                size="small"
              />
            </Grid>

            {/* Tags */}
            <Grid size={{ xs: 12 }}>
              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={formData.tags || []}
                onChange={handleTagChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Tags"
                    placeholder="Add tags..."
                    size="small"
                    helperText="Press Enter to add tag"
                  />
                )}
                ChipProps={{
                  size: 'small',
                }}
              />
            </Grid>

            {/* Specifications */}
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" fontWeight={700}>
                  Specifications
                </Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={addSpecification}>
                  Add Specification
                </Button>
              </Box>
              {formData.specifications && formData.specifications.map((spec, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <TextField
                    placeholder="Key"
                    value={spec.key || ''}
                    onChange={(e) => handleSpecificationChange(index, 'key', e.target.value)}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    placeholder="Value"
                    value={spec.value || ''}
                    onChange={(e) => handleSpecificationChange(index, 'value', e.target.value)}
                    size="small"
                    sx={{ flex: 2 }}
                  />
                  <IconButton size="small" color="error" onClick={() => removeSpecification(index)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => {
            setCreateProductDialog(false);
            setEditProductDialog(false);
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={createProductDialog ? createProduct : updateProduct}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 700,
              background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryLight})`,
            }}
          >
            {createProductDialog ? 'Create Product' : 'Update Product'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Product Dialog */}
      <Dialog
        open={deleteProductDialog}
        onClose={() => setDeleteProductDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4 } }}
      >
        <DialogTitle sx={{ p: 3, pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CancelIcon sx={{ color: COLORS.error }} />
            <Typography variant="h6" fontWeight={700}>Delete Product</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: 1 }}>
          <Typography variant="body2" sx={{ color: COLORS.grey[600] }}>
            Are you sure you want to delete the product <strong>{selectedProduct?.name}</strong>?
          </Typography>
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
            This action cannot be undone. All associated data (images, specifications, tags) will be removed.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setDeleteProductDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => deleteProduct(selectedProduct?.id)}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
          >
            Delete Product
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Action Dialog */}
      <Dialog
        open={bulkActionDialog}
        onClose={() => setBulkActionDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4 } }}
      >
        <DialogTitle sx={{ p: 3, pb: 2 }}>
          <Typography variant="h6" fontWeight={700}>Bulk Action</Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: 1 }}>
          <Typography variant="body2" sx={{ color: COLORS.grey[600], mb: 2 }}>
            Select action for {selectedProducts.length} products:
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              value={bulkAction}
              onChange={(e) => setBulkAction(e.target.value)}
              displayEmpty
            >
              <MenuItem value="" disabled>Select action</MenuItem>
              <MenuItem value="activate">Activate All</MenuItem>
              <MenuItem value="deactivate">Deactivate All</MenuItem>
              <MenuItem value="delete">Delete All</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setBulkActionDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={bulkUpdateProducts}
            disabled={!bulkAction}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 700,
              background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryLight})`,
            }}
          >
            Apply to {selectedProducts.length} Products
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        TransitionComponent={Slide}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ borderRadius: 3, fontWeight: 600 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductManagement;